import { useCallback, useContext, useEffect, useRef } from 'preact/hooks'
import { AthosBeacon } from 'libs/athos/classes/athos-beacon'
import { createContext } from 'preact'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { useIntersectionObserver } from '@/components/hooks/useIntersectionObserver'

const defaultValue = {
  analytics: undefined,
  responseId: undefined
}

const BeaconAnalyticsContext = createContext(defaultValue)

export function useBeaconAnalytics() {
  const context = useContext(BeaconAnalyticsContext)
  return context ?? defaultValue
}

/**
 * Provides a Beacon 2.0 tracker for a results surface. Must be rendered inside
 * the matching Athos provider so it can read the `responseId` returned by the
 * API — every Beacon 2.0 event references it.
 *
 * @param type — Beacon surface family: 'search' | 'autocomplete' | 'category' | 'recommendations'
 * @param profile — recommendation profile tag; recommendations surfaces only
 */
export function BeaconAnalyticsProvider({ type = 'recommendations', profile, children }) {
  const { current: analytics } = useRef(new AthosBeacon(type, profile))
  const { results } = useAthosContext()
  const responseId = results?.responseId

  // Assign synchronously (not in an effect) so child hooks that fire on this
  // same commit always see the current responseId on the tracker instance.
  if (responseId) analytics.setResponseId(responseId)

  const value = { analytics, responseId }

  return <BeaconAnalyticsContext.Provider value={value}>{children}</BeaconAnalyticsContext.Provider>
}

/**
 * Fires a `render` event once per API response (keyed on `responseId`) when the
 * surface has results — a refinement (new query, filter, page) produces a new
 * responseId and re-reports. Pass `enabled: false` to hold events while the
 * surface is not visible (e.g. an inactive recommendations tab).
 */
export function useBeaconLogRender({ enabled = true } = {}) {
  const { analytics, responseId } = useBeaconAnalytics()
  const { results } = useAthosContext()
  const lastFired = useRef(null)

  useEffect(() => {
    if (!enabled || !analytics) return
    if (!responseId || lastFired.current === responseId) return
    if (!results?.results?.length) return
    lastFired.current = responseId
    analytics.render()
  }, [enabled, analytics, responseId, results?.results?.length])
}

/**
 * Fires an `impression` event when the surface scrolls into view, once per
 * `responseId`. Batches the surface's results in a single event (explicitly
 * permitted for personalization surfaces).
 */
export function useBeaconLogImpressions(ref, { enabled = true } = {}) {
  const { analytics, responseId } = useBeaconAnalytics()
  const { results } = useAthosContext()
  const { setNode, entry } = useIntersectionObserver({ threshold: 0.1 })
  const lastFired = useRef(null)

  useEffect(() => {
    if (ref) setNode(ref)
  }, [ref])

  useEffect(() => {
    if (!enabled || !analytics) return
    if (!entry?.isIntersecting) return
    if (!responseId || lastFired.current === responseId) return
    if (!results?.results?.length) return
    lastFired.current = responseId
    analytics.impression(results.results)
  }, [enabled, analytics, entry, responseId, results?.results?.length])
}

/**
 * Returns a handler that fires a `clickthrough` event for the clicked result.
 * Uses `sendBeacon`/`keepalive` so the event survives the navigation.
 */
export function useBeaconLogClick() {
  const { analytics } = useBeaconAnalytics()

  return useCallback((result) => {
    if (!analytics) return
    analytics.clickthrough(result)
  }, [analytics])
}
